import { Placement, autoUpdate, computePosition, flip, offset, shift } from "@floating-ui/dom";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { StyleSheet, View, ViewProps } from "react-native";
import { animDuration, colors, radii } from "../utils/design";
import { FocusTrap } from "./FocusTrap";

const styles = StyleSheet.create({
  container: {
    position: "absolute",
    top: 0,
    left: 0,
    backgroundColor: colors.neutral[700],
    borderRadius: radii[4],
    outlineWidth: 0,
  },
  enterAnimation: {
    animationKeyframes: {
      from: {
        opacity: 0,
        transform: "scale(0.96)",
      },
      to: {
        opacity: 1,
        transform: "scale(1)",
      },
    },
    animationDuration: animDuration[200],
  },
  leaveAnimation: {
    animationKeyframes: {
      from: {
        opacity: 1,
        transform: "scale(1)",
      },
      to: {
        opacity: 0,
        transform: "scale(0.96)",
      },
    },
    animationDuration: animDuration[200],
    animationFillMode: "forwards",
  },

  separator: {
    height: 1,
    backgroundColor: colors.neutral[600],
  },
});

type Props = {
  target: React.MutableRefObject<View | null>;
  opened: boolean;
  placement: Placement;
  offset?: number;
  fitTargetWidth?: boolean;
  children: React.ReactNode;
  onClose: () => void;
};

export const Popover: React.FC<Props> = ({
  target,
  opened,
  placement,
  offset: offsetValue = 4,
  fitTargetWidth = false,
  children,
  onClose,
}) => {
  const ref = useRef<View | null>(null);
  const onCloseRef = useRef(onClose);
  const openedRef = useRef(opened);
  const [state, setState] = useState<"hidden" | "visible" | "closing">(() =>
    opened ? "visible" : "hidden",
  );

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const wasOpened = openedRef.current;
    openedRef.current = opened;

    if (!wasOpened && opened) {
      setState("visible");
    }

    if (wasOpened && !opened) {
      setState("closing");

      const handleAnimationEnd = () => {
        setState("hidden");
      };

      const popoverElement = ref.current as HTMLDivElement | null;
      popoverElement?.addEventListener("animationend", handleAnimationEnd);

      return () => {
        popoverElement?.removeEventListener("animationend", handleAnimationEnd);
      };
    }
  }, [opened]);

  useEffect(() => {
    if (state !== "visible") {
      return;
    }

    const targetElement = target.current as HTMLDivElement | null;
    const popoverElement = ref.current as HTMLDivElement | null;

    if (targetElement == null || popoverElement == null) {
      return;
    }

    const cleanup = autoUpdate(targetElement, popoverElement, () => {
      // We need to check if the elements are still in the DOM
      // because the cleanup function is called after the elements are removed
      const targetElement = target.current as HTMLDivElement | null;
      const popoverElement = ref.current as HTMLDivElement | null;

      if (targetElement == null || popoverElement == null) {
        return;
      }

      computePosition(targetElement, popoverElement, {
        placement,
        strategy: "absolute",
        middleware: [offset(offsetValue), flip(), shift()],
      }).then((position) => {
        popoverElement.style.top = `${position.y}px`;
        popoverElement.style.left = `${position.x}px`;

        if (fitTargetWidth) {
          popoverElement.style.width = `${targetElement.offsetWidth}px`;
        }
      });
    });

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onCloseRef.current();
      }
    };

    const handleClickedOutside = (event: MouseEvent) => {
      if (event.target == null) {
        return;
      }

      if (
        !popoverElement.contains(event.target as Node) &&
        !targetElement.contains(event.target as Node)
      ) {
        onCloseRef.current();
      }
    };

    document.addEventListener("keydown", handleEscape);
    document.addEventListener("mousedown", handleClickedOutside);

    return () => {
      cleanup();
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickedOutside);
    };
  }, [state]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state === "hidden") {
    return null;
  }

  return createPortal(
    <FocusTrap
      ref={ref}
      style={[
        styles.container,
        state === "visible" ? styles.enterAnimation : styles.leaveAnimation,
      ]}
    >
      {children}
    </FocusTrap>,
    document.body,
  );
};

type PopoverContentProps = ViewProps & {
  paddingHorizontal?: number;
  paddingVertical?: number;
};

export const PopoverContent: React.FC<PopoverContentProps> = ({
  paddingHorizontal = 10,
  paddingVertical = 12,
  style,
  ...props
}) => {
  return (
    <View
      style={[
        {
          paddingHorizontal,
          paddingVertical,
        },
        style,
      ]}
      {...props}
    />
  );
};

export const PopoverSeparator: React.FC = () => {
  return <View role="separator" style={styles.separator} />;
};
